import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { LEVELS, LEVEL_LABEL, PERM_MODULES } from '@/lib/perm';
import { ModuleIcon } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const LEVEL_TONE = { no: 'bg-slate-100 text-slate-600', view: 'bg-sky-50 text-sky-700', user: 'bg-amber-50 text-amber-800', manager: 'bg-violet-50 text-violet-700', admin: 'bg-emerald-50 text-emerald-700' };

/** Per-module permission editor for one member. Uses PATCH /companies/{id}/members/{uid}/permissions. */
export const PermissionDialog = ({ member, companyId, modules, onClose, onSaved }) => {
  const { lang } = useLang();
  const id = lang === 'id';
  const [perms, setPerms] = useState(() => Object.fromEntries(PERM_MODULES.map((k) => [k, member?.permissions_raw?.[k] || member?.permissions?.[k] || 'manager'])));
  const [busy, setBusy] = useState(false);
  const isAdminRole = ['owner', 'admin'].includes(member?.role);
  const label = (k) => { const m = modules?.find((x) => x.key === k); return m ? (id ? m.label_id : m.label_en) : k; };
  const setAll = (lvl) => setPerms(Object.fromEntries(PERM_MODULES.map((k) => [k, lvl])));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/companies/${companyId}/members/${member.user_id}/permissions`, { permissions: perms, replace: true });
      toast.success(id ? `Izin ${member.full_name || member.email} disimpan` : 'Permissions saved');
      onSaved?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="permission-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck size={18} />{id ? 'Izin akses' : 'Access permissions'} · {member?.full_name || member?.email}</DialogTitle>
          <DialogDescription>{id ? 'Tentukan level akses per modul. No = tidak bisa masuk · View = hanya lihat · User = lihat & ubah data sendiri · Manager = semua data modul · Administrator = semua data + Pengaturan modul.' : 'Set the access level per module. No = blocked · View = read-only · User = own records · Manager = all module data · Administrator = all data + module settings.'}</DialogDescription>
        </DialogHeader>
        {isAdminRole ? (
          <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="permission-admin-note">{id ? `Peran "${member.role}" otomatis mendapat level Administrator di semua modul. Ubah perannya menjadi "member" untuk mengatur izin per modul.` : `Role "${member.role}" automatically gets Administrator on every module. Change the role to "member" to configure per-module permissions.`}</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">{id ? 'Set semua:' : 'Set all:'}{LEVELS.map((l) => <button type="button" key={l} onClick={() => setAll(l)} className={`rounded-full px-2.5 py-1 font-medium ${LEVEL_TONE[l]} hover:opacity-80`} data-testid={`permission-set-all-${l}`}>{LEVEL_LABEL[l][lang]}</button>)}</div>
            <div className="divide-y divide-border rounded-xl border border-border">
              {PERM_MODULES.map((k) => (
                <div key={k} className="flex items-center gap-3 px-3 py-2" data-testid={`permission-row-${k}`}>
                  <ModuleIcon moduleKey={k} size="sm" />
                  <div className="flex-1 text-sm font-medium">{label(k)}</div>
                  <Select value={perms[k]} onValueChange={(v) => setPerms({ ...perms, [k]: v })}>
                    <SelectTrigger className={`h-8 w-48 rounded-lg text-xs ${LEVEL_TONE[perms[k]]}`} data-testid={`permission-select-${k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_LABEL[l][lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-xl" onClick={onClose}>{id ? 'Tutup' : 'Close'}</Button>
          {!isAdminRole && <Button type="button" className="rounded-xl" disabled={busy} onClick={save} data-testid="permission-save">{busy ? <Loader2 size={16} className="animate-spin" /> : (id ? 'Simpan izin' : 'Save permissions')}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Compact summary badges for the members table. */
export const PermSummary = ({ member, lang }) => {
  if (['owner', 'admin'].includes(member.role)) return <Badge variant="outline" className={`rounded-full ${LEVEL_TONE.admin}`}>{lang === 'id' ? 'Semua modul · Administrator' : 'All modules · Administrator'}</Badge>;
  const counts = PERM_MODULES.reduce((a, k) => { const l = member.permissions?.[k] || 'manager'; a[l] = (a[l] || 0) + 1; return a; }, {});
  return <div className="flex flex-wrap gap-1">{LEVELS.filter((l) => counts[l]).map((l) => <Badge key={l} variant="outline" className={`rounded-full text-[10px] ${LEVEL_TONE[l]}`}>{counts[l]} {LEVEL_LABEL[l][lang].split(' ')[0]}</Badge>)}</div>;
};
